import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Alert, MenuItem, Autocomplete } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { employeesApi, DEPARTMENTS, type Employee, type CreateEmployeeInput } from '../../api/employees.api';

interface Props {
  open: boolean;
  initial?: Employee | null;
  onClose: () => void;
  onSubmit: (input: CreateEmployeeInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateEmployeeInput = { name: '' };

export function EmployeeFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateEmployeeInput>(empty);

  // Manager picker: searchable by NAME, persisted as reportingManagerId.
  const { data: managers } = useQuery({
    queryKey: ['employees-managers'],
    queryFn: () => employeesApi.list({ limit: 500 }),
    enabled: open,
  });
  const managerOptions = (managers?.data ?? []).filter((m) => m.id !== initial?.id); // can't manage self

  useEffect(() => {
    setForm(
      initial
        ? {
            employeeCode: initial.employeeCode,
            name: initial.name,
            department: (initial.department as CreateEmployeeInput['department']) ?? null,
            designation: initial.designation ?? undefined,
            reportingManagerId: initial.reportingManagerId,
            email: initial.email ?? undefined,
            mobile: initial.mobile ?? undefined,
          }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateEmployeeInput>(k: K, v: CreateEmployeeInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (): void => {
    // employee_code is immutable — never send it on edit (update schema rejects it).
    const { employeeCode, ...rest } = form;
    void employeeCode;
    void onSubmit(isEdit ? rest : form);
  };

  // Resolve the currently-selected manager for the Autocomplete value (fall back to the
  // name the API already returned, so the field shows correctly before the list loads).
  const selectedManager =
    managerOptions.find((m) => m.id === form.reportingManagerId) ??
    (initial && initial.reportingManagerId === form.reportingManagerId && initial.reportingManagerName
      ? ({ id: initial.reportingManagerId, name: initial.reportingManagerName } as Employee)
      : null);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Employee — ${initial?.employeeCode}` : 'Create Employee'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          {/* Employee ID — manually entered & unique on create; immutable on edit. */}
          <TextField label="Employee ID" required={!isEdit} disabled={isEdit}
            value={isEdit ? (initial?.employeeCode ?? '') : (form.employeeCode ?? '')}
            onChange={(e) => set('employeeCode', e.target.value)}
            helperText={isEdit ? 'Not editable after creation' : 'Enter a unique Employee ID'} fullWidth />
          <TextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select label="Department" value={form.department ?? ''}
              onChange={(e) => set('department', (e.target.value || null) as CreateEmployeeInput['department'])} fullWidth>
              <MenuItem value="">—</MenuItem>
              {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField label="Designation" value={form.designation ?? ''} onChange={(e) => set('designation', e.target.value)} fullWidth />
          </Stack>
          <Autocomplete
            options={managerOptions}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedManager}
            onChange={(_e, v) => set('reportingManagerId', v ? v.id : null)}
            renderInput={(params) => <TextField {...params} label="Reporting Manager Name" placeholder="Search by name…" />}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} fullWidth />
            <TextField label="Mobile" value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
